import React from 'react'
import { formatCurrency } from '@/lib/utils'
import DiscountRow from '@/components/payments/DiscountRow'
import type { DiscountType } from '@/lib/totals'

/**
 * TotalsPanel — the shared inner totals card (Subtotal / Discount / Tax /
 * Total [+ payment rows]) extracted byte-for-byte from InvoiceDetail,
 * QuoteDetail, and RecurringInvoiceDetail. Each call site keeps its own outer
 * <div flex justify-end marginBottom:X> wrapper; this renders the width:280
 * card only.
 *
 * Behavior-preserving notes (do NOT "improve"):
 *  - formatCurrency(subtotal/taxAmount/total) take NO currency arg (default
 *    USD), matching all three original sites. currency flows ONLY to
 *    DiscountRow. Pre-existing latent inconsistency — preserved deliberately.
 *  - Payment rows render only when amountPaid != null && amountPaid > 0
 *    (InvoiceDetail's exact conditional). Quotes/series never pass them.
 *  - isLocked drives tax editability (readonly "{taxRate}%" vs editable input)
 *    and is forwarded to DiscountRow. Defaults false (RecurringInvoiceDetail's
 *    always-editable case is isLocked={false}).
 */
interface TotalsPanelProps {
  subtotal: number
  discountType: DiscountType
  discountValue: number
  discountAmount: number
  onDiscountTypeChange: (t: DiscountType) => void
  onDiscountValueChange: (v: number) => void
  taxRate: number
  taxAmount: number
  onTaxRateChange: (v: number) => void
  total: number
  currency?: string
  isLocked?: boolean
  amountPaid?: number
  amountDue?: number
}

export default function TotalsPanel({
  subtotal, discountType, discountValue, discountAmount,
  onDiscountTypeChange, onDiscountValueChange,
  taxRate, taxAmount, onTaxRateChange, total,
  currency, isLocked = false, amountPaid, amountDue,
}: TotalsPanelProps) {
  return (
    <div style={{ width: 280, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)' }}>
        <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>Subtotal</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{formatCurrency(subtotal)}</span>
      </div>
      <DiscountRow
        isLocked={isLocked}
        discountType={discountType}
        discountValue={discountValue}
        discountAmount={discountAmount}
        currency={currency}
        onTypeChange={t => { onDiscountTypeChange(t); if (t === 'none') onDiscountValueChange(0) }}
        onValueChange={onDiscountValueChange}
      />
      <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>Tax</span>
          {!isLocked && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="number" value={taxRate} onChange={e => onTaxRateChange(parseFloat(e.target.value) || 0)}
                style={{ width: 40, padding: '2px 6px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 5, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', textAlign: 'center' }} />
              <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>%</span>
            </div>
          )}
          {isLocked && <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{taxRate}%</span>}
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{formatCurrency(taxAmount)}</span>
      </div>
      <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', background: 'var(--accent-dim)' }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)' }}>Total</span>
        <span style={{ fontSize: 18, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>{formatCurrency(total)}</span>
      </div>
      {amountPaid != null && amountPaid > 0 && (
        <>
          <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border-subtle)' }}>
            <span style={{ fontSize: 13, color: 'var(--positive)' }}>Amount paid</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--positive)' }}>{formatCurrency(amountPaid)}</span>
          </div>
          <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', background: 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--danger)' }}>Amount due</span>
            <span style={{ fontSize: 15, fontWeight: 900, color: 'var(--danger)' }}>{formatCurrency(amountDue ?? 0)}</span>
          </div>
        </>
      )}
    </div>
  )
}
